import React, { Component } from 'react';
import Wahanda from 'common/wahanda';
import { PERMISSIONS } from 'common/constants/userPermissions';
import { DisabledWithTooltip } from 'components/common/DisabledWithTooltip';
import ReactDialog from 'components/common/react-dialog';
import { DialogFooter, DialogFooterButton } from 'components/common/DialogFooter';
import { Checkbox } from 'components/common/Checkbox';
import {
  DISABLE_REASON,
  DisableReason,
  canGrantPermission,
  isImpliedPermission,
  mergePermissions,
} from 'components/employees/employeeEditPolicy';
import style from './style.scss';

const lang = Wahanda.lang.settings.employees.employee.permissions;
const DIALOG_WIDTH = 600;

interface PermissionRenderOptions {
  disabled?: boolean;
  forceChecked?: boolean;
  disabledReason?: DisableReason;
}

interface IEmployeeCustomPermissionsDialogProps extends React.HTMLAttributes<Element> {
  permissions?: string[];
  actorPermissions?: string[];
  pointOfSalePermissionsVisible?: boolean;
  readOnly?: boolean;
  readOnlyReason?: DisableReason;
  onClose: (...args: any[]) => any;
  onSave: (...args: any[]) => any;
}

type EmployeeCustomPermissionsDialogState = {
  permissions: string[];
};

export default class EmployeeCustomPermissionsDialog extends Component<
  IEmployeeCustomPermissionsDialogProps,
  EmployeeCustomPermissionsDialogState
> {
  static defaultProps = {
    permissions: [],
    actorPermissions: [],
    pointOfSalePermissionsVisible: false,
    readOnly: false,
  };

  state = {
    permissions: [...(this.props.permissions ?? [])],
  };

  onSave = () => {
    this.props.onSave(
      mergePermissions(
        this.props.permissions ?? [],
        this.state.permissions,
        this.props.actorPermissions ?? [],
      ),
    );
  };

  onChange = (checked, event) => {
    const { value } = event.target;
    this.setState(({ permissions }) => ({
      permissions: checked
        ? [...permissions, value]
        : permissions.filter((permission) => permission !== value),
    }));
  };

  isChecked = (permission: string) => this.state.permissions.indexOf(permission) !== -1;

  getPermissionOptions = (value: string): PermissionRenderOptions => {
    if (this.props.readOnly) {
      return {
        disabled: true,
        forceChecked: isImpliedPermission(value) || this.isChecked(value),
        disabledReason: this.props.readOnlyReason ?? DISABLE_REASON.NO_ADD_THERAPISTS,
      };
    }

    if (isImpliedPermission(value)) {
      return {
        disabled: true,
        forceChecked: true,
        disabledReason: DISABLE_REASON.PERMISSION_IMPLIED,
      };
    }

    if (!canGrantPermission(this.props.actorPermissions ?? [], value)) {
      const targetHasPermission = (this.props.permissions ?? []).includes(value);
      return {
        disabled: true,
        forceChecked: isImpliedPermission(value) || targetHasPermission,
        disabledReason: DISABLE_REASON.PERMISSION_NOT_HELD_BY_ACTOR,
      };
    }

    return {
      disabled: false,
      forceChecked: false,
    };
  };

  renderPermission = (value: string, label: string, options: PermissionRenderOptions = {}) => {
    const { disabled = false, forceChecked = false, disabledReason } = options;
    const checkbox = (
      <Checkbox
        name={value}
        checked={this.isChecked(value) || forceChecked}
        value={value}
        label={label}
        onChange={disabled ? undefined : this.onChange}
        disabled={disabled}
      />
    );

    return (
      <div className={style.checkboxContainer}>
        {disabled && disabledReason ? (
          <DisabledWithTooltip reason={disabledReason}>{checkbox}</DisabledWithTooltip>
        ) : (
          checkbox
        )}
      </div>
    );
  };

  render() {
    const { pointOfSalePermissionsVisible, onClose, readOnly } = this.props;

    return (
      <ReactDialog
        dataTest="employee-permissions-modal"
        title={lang.title}
        onClose={onClose}
        width={DIALOG_WIDTH}
        noContentPadding
        noContentTopBorders
      >
        <div className={style.dialogContent}>
          <div className={style.column}>
            <div className={style.group}>
              <h3 className={style.groupTitle}>{lang.general.title}</h3>
              {this.renderPermission(
                PERMISSIONS.viewBookings,
                lang.general.viewBookings,
                this.getPermissionOptions(PERMISSIONS.viewBookings),
              )}
              {this.renderPermission(
                PERMISSIONS.manageVenueDetails,
                lang.general.manageVenue,
                this.getPermissionOptions(PERMISSIONS.manageVenueDetails),
              )}
              {this.renderPermission(
                PERMISSIONS.viewFinanceData,
                lang.general.viewFinance,
                this.getPermissionOptions(PERMISSIONS.viewFinanceData),
              )}
              {this.renderPermission(
                PERMISSIONS.viewReportsSales,
                lang.general.viewReportsSales,
                this.getPermissionOptions(PERMISSIONS.viewReportsSales),
              )}
              {this.renderPermission(
                PERMISSIONS.cancelTransaction,
                lang.general.cancelTransaction,
                this.getPermissionOptions(PERMISSIONS.cancelTransaction),
              )}
              {this.renderPermission(
                PERMISSIONS.canEditServicePrice,
                lang.general.canEditServicePrice,
                this.getPermissionOptions(PERMISSIONS.canEditServicePrice),
              )}
              {pointOfSalePermissionsVisible &&
                this.renderPermission(
                  PERMISSIONS.managePos,
                  lang.general.managePos,
                  this.getPermissionOptions(PERMISSIONS.managePos),
                )}
            </div>

            <div className={style.group}>
              <h3 className={style.groupTitle}>{lang.menu.title}</h3>
              {this.renderPermission(
                PERMISSIONS.viewMenu,
                lang.menu.viewMenu,
                this.getPermissionOptions(PERMISSIONS.viewMenu),
              )}
              {this.renderPermission(
                PERMISSIONS.editMenu,
                lang.menu.editMenu,
                this.getPermissionOptions(PERMISSIONS.editMenu),
              )}
            </div>

            <div className={style.group}>
              <h3 className={style.groupTitle}>{lang.clients.clientsAndMarketing}</h3>
              {this.renderPermission(
                PERMISSIONS.viewClientList,
                lang.clients.viewClientList,
                this.getPermissionOptions(PERMISSIONS.viewClientList),
              )}
              {this.renderPermission(
                PERMISSIONS.createClients,
                lang.clients.createClients,
                this.getPermissionOptions(PERMISSIONS.createClients),
              )}
              {this.renderPermission(
                PERMISSIONS.viewClientContactDetails,
                lang.clients.viewEditClient,
                this.getPermissionOptions(PERMISSIONS.viewClientContactDetails),
              )}
              {this.renderPermission(
                PERMISSIONS.accessMarketingTools,
                lang.clients.accessMarketingTools,
                this.getPermissionOptions(PERMISSIONS.accessMarketingTools),
              )}
            </div>
          </div>

          <div className={style.column}>
            <div className={style.group}>
              <h3 className={style.groupTitle}>{lang.calendar.title}</h3>
              {this.renderPermission(
                PERMISSIONS.viewOwnCalendar,
                lang.calendar.viewOwn,
                this.getPermissionOptions(PERMISSIONS.viewOwnCalendar),
              )}
              {this.renderPermission(
                PERMISSIONS.editOwnCalendar,
                lang.calendar.editOwn,
                this.getPermissionOptions(PERMISSIONS.editOwnCalendar),
              )}
              {this.renderPermission(
                PERMISSIONS.viewAnyCalendar,
                lang.calendar.viewOthers,
                this.getPermissionOptions(PERMISSIONS.viewAnyCalendar),
              )}
              {this.renderPermission(
                PERMISSIONS.editAnyCalendar,
                lang.calendar.addEditOthers,
                this.getPermissionOptions(PERMISSIONS.editAnyCalendar),
              )}
              {this.renderPermission(
                PERMISSIONS.deleteAppointments,
                lang.general.canDeleteAppointments,
                this.getPermissionOptions(PERMISSIONS.deleteAppointments),
              )}
            </div>

            <div className={style.group}>
              <h3 className={style.groupTitle}>{lang.staff.title}</h3>
              {this.renderPermission(
                PERMISSIONS.addTherapists,
                lang.staff.add,
                this.getPermissionOptions(PERMISSIONS.addTherapists),
              )}
              {this.renderPermission(
                PERMISSIONS.editUserPermissions,
                lang.staff.changePermissions,
                this.getPermissionOptions(PERMISSIONS.editUserPermissions),
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <DialogFooterButton
            title={Wahanda.lang.shared.buttons.cancel}
            type="secondary"
            onClick={onClose}
          />
          <DialogFooterButton
            title={Wahanda.lang.shared.buttons.save}
            disabled={readOnly}
            onClick={this.onSave}
          />
        </DialogFooter>
      </ReactDialog>
    );
  }
}
